/**
 * FAZ 5 M5 — NumericInput (0-deletion fix)
 *
 * Kullanıcı yazarken input string olarak boş kalabilir; parent'a giden
 * değer normalize edilir. `allowEmpty=false` (varsayılan) ise boş → 0.
 *
 * Kritik davranış:
 *   - value=0 gelse bile kullanıcı Backspace ile silebilir (raw="", not "0")
 *   - Yarım decimal ("0.", "12,") düzenleme sırasında bozulmaz
 *   - Blur'da normalize edilir + parent'a final değer gönderilir
 *   - Türkçe locale: "0,28" → "0.28" (backend her zaman "." bekler)
 *
 * Kullanım:
 *   <NumericInput
 *     value={form.qty}
 *     onChange={(v) => setForm({...form, qty: v})}
 *     min={0} step="0.01" decimal placeholder="0,00"
 *   />
 */
import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

const toRaw = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

const NumericInput = forwardRef(function NumericInput(
  {
    value,
    onChange,
    decimal = true,
    min,
    max,
    step,
    allowEmpty = false, // default: submit'te boş → 0
    onBlur,
    ...rest
  },
  ref,
) {
  const [raw, setRaw] = useState(toRaw(value));
  const focused = useRef(false);
  const lastEmitted = useRef(value);

  // Dışarıdan value değişirse VE kullanıcı input'a odaklı değilse senkronize et.
  // Bu, form reset (setProdForm(empty)) sonrası input'un temizlenmesini sağlar.
  useEffect(() => {
    if (focused.current) return;
    if (value === lastEmitted.current) return;
    setRaw(toRaw(value));
  }, [value]);

  const emit = (next) => {
    lastEmitted.current = next;
    onChange?.(next);
  };

  const handleChange = (e) => {
    let v = e.target.value;
    if (decimal) v = v.replace(",", ".");
    setRaw(v);

    if (v === "" || v === "-" || v === "." || v === "-.") {
      // Kullanıcı düzenliyor; parent'a boş sinyal gönder (submit'te normalize)
      emit(allowEmpty ? null : "");
      return;
    }
    const n = decimal ? parseFloat(v) : parseInt(v, 10);
    if (!isNaN(n)) emit(n);
  };

  const handleBlur = (e) => {
    focused.current = false;
    // Yarım / geçersiz input → normalize
    if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
      const final = allowEmpty ? null : 0;
      setRaw(allowEmpty ? "" : "0");
      emit(final);
    } else {
      const n = decimal ? parseFloat(raw.replace(",", ".")) : parseInt(raw, 10);
      if (isNaN(n)) {
        const final = allowEmpty ? null : 0;
        setRaw(allowEmpty ? "" : "0");
        emit(final);
      } else {
        setRaw(String(n));
        emit(n);
      }
    }
    onBlur?.(e);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode={decimal ? "decimal" : "numeric"}
      value={raw}
      onChange={handleChange}
      onFocus={() => { focused.current = true; }}
      onBlur={handleBlur}
      min={min}
      max={max}
      step={step}
      {...rest}
    />
  );
});

export default NumericInput;
